import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isNil, map, omitBy, isEmpty, groupBy } from "lodash";
import {
  SelectInput,
  Button,
  BasicModal,
  TextInput,
  DataTable,
  ActionMenu,
  AlertBox,
} from "@life-line-lab/components-react";
import {
  RightCircleOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Col, Row, Spin } from "antd";
import { CSVLink } from "react-csv";
import { ToTitleCase } from "Helpers/to-title-case";
import { useSession } from "Hooks/index";
import CSVFileUpload from "Components/shared/csv-fileupload";
import MassOrderInfoDisplay from "../shared/parcel-modals/mass-order-info-display";
import { ParcelTypeEnums } from "Data/order";

const csvData = [
  [
    "No",
    "Sender Name",
    "Sender Email",
    "Sender Contact",
    "Sender Location",
    "Sender Unit",
    "Sender Street1",
    "Sender Street2",
    "Sender City",
    "Sender State",
    "Sender Postcode",
    "Sender Country",
    "Recipient Name",
    "Recipient Email",
    "Recipient Contact",
    "Recipient Street1",
    "Recipient Street2",
    "Recipient City",
    "Recipient State",
    "Recipient Postcode",
    "Recipient Country",
    "Delivery Type",
    "Service Type",
    "Special Care",
    "Special Condition",
    "Tracking No",
    "Weight in kg",
    "Length in cm",
    "Width in cm",
    "Height in cm",
    "Currency",
    "Type of Goods",
    "Value of Goods",
    "Has Insurance",
    "Remarks"
  ],
  [
    "1",
    "Jazzlynn Ser",
    "mel@gmail.com",
    "6011-55555555",
    "Primary Address",
    "64-3",
    "Jalan 27/70a",
    "Desa Sri Hartamas",
    "Kuala Lumpur",
    "Kuala Lumpur",
    "17700",
    "MY",
    "Melissa Jane Doe",
    "mel@gmail.com",
    "60122222222",
    "Pangspuri Seroja 5",
    "47810",
    "Petaling Jaya",
    "Selangor",
    "40600",
    "MY",
    "Mid-Mile Only",
    "Next Day Delivery",
    "Fragile",
    "Lift Up",
    "TR-20210012345",
    "5",
    "50",
    "100",
    "100",
    "RM",
    "Electronics",
    "20",
    "TRUE",
    "Example remarks."
  ],
  [
    "2",
    "Alex",
    "mel@gmail.com",
    "60181111111",
    "Primary Address",
    "64-3",
    "Jalan 27/70a",
    "Desa Sri Hartamas",
    "Kuala Lumpur",
    "Kuala Lumpur",
    "17700",
    "MY",
    "Melissa Jane Doe",
    "mel@gmail.com",
    "60134444444",
    "Pangspuri Seroja 5",
    "47810",
    "Petaling Jaya",
    "Selangor",
    "40600",
    "MY",
    "Mid-Mile Only",
    "Next Day Delivery",
    "Fragile",
    "Lift Up",
    "TR-20210012345",
    "5",
    "100",
    "50",
    "100",
    "RM",
    "Stationary",
    "20",
    "FALSE",
    "Example remarks."
  ],
];

interface InboundData {
  no: string;
  currency: string;
  sender_country_code: string;
  sender_number: string;
  sender_location: string;
  recipient_country_code: string;
  recipient_number: string;
  recipient_country: string;
  recipient_postcode: string;
  recipient_city: string;
  recipient_state: string;
  recipient_street1: string;
  sender_address: string;
  recipient_name: string;
  recipient_email: string;
  recipient_contact: string;
  delivery_address: string;
  delivery_type: string;
  service_type: string;
  tracking_no: string;
  special_condition: string;
  special_care: string;
  weight_in_kg: string;
  width_in_cm: string;
  length_in_cm: string;
  height_in_cm: string;
  type_of_goods: string;
  value_of_goods: string;
  has_insurance: boolean;
  remarks: string
}

const errorMap = {
  LOCATION: "deliveryAddress",
  TYPE_OF_GOODS: "typeOfGoods",
  DELIVERY_TYPE: "deliveryType",
  SERVICE_TYPE: "serviceType",
  INSURANCE: "insurance",
  SPECIAL_CONDITION: "specialCondition",
  SPECIAL_CARE: "specialCare",
};

const UploadBulkOrderComponent = ({
  handleBulkValidation,
  handleBulkCreate,
  excelOrder,
  customerOptions,
  calculatedGrandTotal,
  allOrderSettingsInfo,
  setExcelOrder,
  duplicateTracking,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { org, roles } = useSession();

  const initialError = {};
  const initialInput = {};
  const initialModal: any = {
    open: false,
    type: "info",
  };

  //    All States
  // ==========================================================
  const [isUploadDisable, setIsUploadDisable] = useState<boolean>(false);
  const [isCustomer, setIsCustomer] = useState<boolean>(false);
  const [grandTotal, setGrandTotal] = useState<any>(calculatedGrandTotal);
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [modal, setModal] = useState<any>(initialModal);
  const [modalContent, setModalContent] = useState<any>(<></>);
  const [errorRow, setErrorRow] = useState<any>([]);

  //    Table Data
  // ==========================================================
  const baseColumns: any = useMemo(() => {
    return [
      {
        title: t("label.recipientName").toLocaleUpperCase(),
        dataIndex: "receiverInfo",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>{record.receiverInfo.fullName || <span>&#11835;</span>}</span>
          );
        },
      },
      {
        title: t("label.status").toLocaleUpperCase(),
        dataIndex: "orderStatus",
        ellipsis: true,
        render: (_: any, record: any, rowIndex: string) => {
          const orderStatus = record?.orderStatusInfo?.orderStatus;

          return (
            <>
              {orderStatus == "OPEN" ? (
                <span className="ub-status success">
                  {t("label.successful")}
                </span>
              ) : orderStatus == "PENDING_REVIEW" ? (
                <span className="ub-status pending-review">
                  {t("label.pendingReview")}
                </span>
              ) : (
                <span className="ub-status failed">
                  {t("label.unsuccessful")}
                </span>
              )}
            </>
          );
        },
      },
      {
        title: t("label.issue").toLocaleUpperCase(),
        dataIndex: "issueType",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {map(record?.orderStatusInfo.issueType, (issue) => {
                let issueAffected = issue.settingAffected.replace("_", " ");
                return (
                  <span>
                    {`${issue?.name} (${issueAffected})` || (
                      <span>&#11835;</span>
                    )}
                    <br />
                  </span>
                );
              })}
            </span>
          );
        },
      },
      {
        title: t("label.recipientEmail").toLocaleUpperCase(),
        dataIndex: "contactEmail",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {record.receiverInfo.contactEmail || <span>&#11835;</span>}
            </span>
          );
        },
      },
      {
        title: t("label.recipientContact").toLocaleUpperCase(),
        dataIndex: "contactNumber",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {`+${record.receiverInfo.contactNumber.fullNumber}` || (
                <span>&#11835;</span>
              )}
            </span>
          );
        },
      },
      {
        title: t("label.deliveryAddress").toLocaleUpperCase(),
        dataIndex: "deliveryAddress",
        ellipsis: true,
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <div>
              <div>
                {record.receiverInfo.address.street1 + ", "}
                {record.receiverInfo.address.street2
                  ? record.receiverInfo.address.street2 + ", "
                  : ""}
                {record.receiverInfo.address.city + ", "}
                {record.receiverInfo.address.state + ", "}
                {record.receiverInfo.address.postcode + ", "}
                {record.receiverInfo.address.country}
              </div>
              <AlertBox text={error?.[`deliveryAddress[${rowIndex}]`]} />
            </div>
          );
        },
      },
      {
        title: t("label.remarks").toLocaleUpperCase(),
        dataIndex: "remarks",
      },
      {
        title: t("label.deliveryType").toLocaleUpperCase(),
        dataIndex: "deliveryType",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <div>
              {!isEmpty(record.additionalCharges) ? (
                map(record.additionalCharges, (type) => {
                  if (type.type === "DELIVERY_TYPE") {
                    return (
                      <React.Fragment>
                        <span>{type.name || <span>&#11835;</span>}</span>
                      </React.Fragment>
                    );
                  }
                })
              ) : (
                <span>Not calculated due to unsuccessful status</span>
              )}
              <AlertBox text={error?.[`deliveryType[${rowIndex}]`]} />
            </div>
          );
        },
      },
      {
        title: t("label.serviceType").toLocaleUpperCase(),
        dataIndex: "serviceType",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <div>
              {!isEmpty(record.additionalCharges) ? (
                map(record.additionalCharges, (type) => {
                  if (type.type === "SERVICE_TYPE") {
                    return <span>{type.name || <span>&#11835;</span>}</span>;
                  }
                })
              ) : (
                <span>Not calculated due to unsuccessful status</span>
              )}
              <AlertBox text={error?.[`serviceType[${rowIndex}]`]} />
            </div>
          );
        },
      },
      {
        title: t("label.trackingNo").toLocaleUpperCase(),
        dataIndex: "trackingNo",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span className="umo-tracking-no">
              {record.trackingNumber || <span>&#11835;</span>}
            </span>
          );
        },
      },
      {
        title: t("label.specialCondition").toLocaleUpperCase(),
        dataIndex: "specialCondition",
        render: (_: any, record: any, rowIndex: string) => {
          const orderStatus = record?.orderStatusInfo?.orderStatus;

          if (orderStatus == "UNSUCCESSFUL")
            return <span>Not calculated due to unsuccessful status</span>;

          const specialCondition =
            orderStatus == "PENDING_REVIEW"
              ? record?.orderStatusInfo?.issueType?.find(
                  (issue) => issue?.settingAffected == "SPECIAL_CONDITION",
                )
              : record?.additionalCharges?.find(
                  (charge) => charge?.type == "SPECIAL_CONDITION",
                );

          // there should only be one special condition
          return specialCondition ? (
            <div>
              <div>
                {specialCondition?.settingName ??
                  specialCondition?.name ??
                  t("txt.none")}
              </div>
              <AlertBox text={error?.[`specialCondition[${rowIndex}]`]} />
            </div>
          ) : (
            <span>{t("txt.none")}</span>
          );
        },
      },
      {
        title: t("label.specialCare").toLocaleUpperCase(),
        dataIndex: "specialCare",
        render: (_: any, record: any, rowIndex: string) => {
          const orderStatus = record?.orderStatusInfo?.orderStatus;

          if (orderStatus == "UNSUCCESSFUL")
            return <span>Not calculated due to unsuccessful status</span>;

          const specialCare =
            orderStatus == "PENDING_REVIEW"
              ? record?.orderStatusInfo?.issueType?.find(
                  (issue) => issue?.settingAffected == "SPECIAL_CARE",
                )
              : record?.additionalCharges?.find(
                  (charge) => charge?.type == "SPECIAL_CARE",
                );

          // there should only be one special care
          return specialCare ? (
            <div>
              <div>
                {specialCare?.settingName ?? specialCare?.name ?? t("txt.none")}
              </div>
              <AlertBox text={error?.[`specialCare[${rowIndex}]`]} />
            </div>
          ) : (
            <span>{t("txt.none")}</span>
          );
        },
      },
      {
        title: t("label.weightInKg").toLocaleUpperCase(),
        dataIndex: "weight",
        render: (_: any, record: any, rowIndex: string) => {
          return <span>{record.weight || <span>&#11835;</span>}</span>;
        },
      },
      {
        title: t("label.widthInCm").toLocaleUpperCase(),
        dataIndex: "width",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {record.dimension.width || <span>&#11835;</span>}
              <AlertBox text={error?.[`rateTable[${rowIndex}]`]} />
            </span>
          );
        },
      },
      {
        title: t("label.lengthInCm").toLocaleUpperCase(),
        dataIndex: "length",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {record.dimension.length || <span>&#11835;</span>}
              <AlertBox text={error?.[`rateTable[${rowIndex}]`]} />
            </span>
          );
        },
      },
      {
        title: t("label.heightInCm").toLocaleUpperCase(),
        dataIndex: "height",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {record.dimension.height || <span>&#11835;</span>}
              <AlertBox text={error?.[`rateTable[${rowIndex}]`]} />
            </span>
          );
        },
      },
      {
        title: t("label.typeOfGoods").toLocaleUpperCase(),
        dataIndex: "typeOfGood",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {map(record?.parcels, (parcel) => {
                return (
                  <span>
                    {parcel?.name || <span>&#11835;</span>}
                    <br />
                  </span>
                );
              })}
            </span>
          );
        },
      },
      {
        title: t("label.valueOfGoods").toLocaleUpperCase(),
        dataIndex: "goodsValue",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              {map(record?.parcels, (parcel) => {
                return (
                  <span>
                    {parcel?.goodsValue || <span>&#11835;</span>}
                    <br />
                  </span>
                );
              })}
            </span>
          );
        },
      },
      {
        title: t("label.estimatedAmount").toLocaleUpperCase(),
        dataIndex: "totalCharges",
        render: (_: any, record: any, rowIndex: string) => {
          return (
            <span>
              <p>RM {parseFloat(record?.totalCharges).toFixed(2)}</p>
            </span>
          );
        },
      },
      {
        title: "",
        dataIndex: "actions",
        render: (_: any, record: any, rowIndex: string) => (
          <ActionMenu
            label={<MoreOutlined />}
            actions={[
              {
                label: t("action.viewAmountBreakdown"),
                onClick: () => {
                  handleChargesBreakdownModal(record);
                },
              },
              {
                label: t("action.remove"),
                onClick: () => {
                  handleDelete(record);
                },
                isDisable: false,
              },
            ]}
          />
        ),
      },
    ];
  }, [error]);

  //    Functions
  // ==========================================================
  // if there are duplicate tracking numbers, find the index and highlight
  useEffect(() => {
    if (!isEmpty(duplicateTracking)) {
      // find the index of the order using tracking number
      for (let i = 0; i < duplicateTracking.length; i++) {
        const currentTracking = duplicateTracking[i];

        // worst case is if all orders have the same tracking number
        for (let j = 0; j < excelOrder.length; j++) {
          if (excelOrder[j].trackingNumber == currentTracking) {
            setErrorRow((prev) => [...prev, j]);
          }
        }
      }
    }
  }, [duplicateTracking]);

  // if there is data error, compare and confirm
  const sanitiseData = (data) => {
    for (let i = 0; i < data?.length; i++) {
      // errors are all found in orderStatusInfo
      const errorData = data[i]?.orderStatusInfo?.issueType?.map(
        (type) => type?.settingAffected,
      );

      // if errorData is not null, the row has errors to be settled
      if (errorData) setErrorRow((prev) => [...prev, i]);

      // for each setting affected, highlight the error
      errorData?.forEach((error) => {
        let errorMsg: any;

        switch (error) {
          case "RATE_TABLE":
            // custom message
            errorMsg = { [`rateTable[${i}]`]: "Exceeded dimensions." };
            break;
          default:
            // mapping depends on settingAffected (BE enums), which is listed in errorMap
            errorMsg = {
              [`${errorMap?.[error]}[${i}]`]: t(`error.pattern`, {
                field: t(`label.${errorMap?.[error]}`),
              }),
            };
        }

        // set error
        setError((prev) => ({ ...prev, ...errorMsg }));
      });
    }
  };

  useEffect(() => {
    if (isEmpty(excelOrder)) {
      setGrandTotal("0.00");
    } else {
      setGrandTotal(calculatedGrandTotal);
    }
    sanitiseData(excelOrder);
    setInput({ ...input, orders: [...excelOrder] });
  }, [calculatedGrandTotal, excelOrder]);

  useEffect(() => {
    if ((roles[0].type = "CUSTOMER")) {
      setIsCustomer(true);
    }
  }, []);

  const blueprint = (inboundData: InboundData[]) => {
    console.debug("inb", inboundData);

    const parseAddress = (data, type) => ({
      street1: data[`${type}_street1`]?.toString(),
      street2: data[`${type}_street2`]?.toString(),
      city: data[`${type}_city`]?.toString(),
      state: data[`${type}_state`]?.toString(),
      postcode: data[`${type}_postcode`]?.toString(),
      country: data[`${type}_country`]?.toString(),
    });

    const groupedData = groupBy(inboundData, (data) => data.no);

    // check for duplicate tracking no to label it as a split shipment
    const trackingNumberCount: Record<string, number> = {};
    inboundData.forEach((data) => {
      if (data.tracking_no) {
        trackingNumberCount[data.tracking_no] =
          (trackingNumberCount[data.tracking_no] || 0) + 1;
      }
    });

    const resultMap = map(groupedData, (group, no) => {
      const recipientContactNumber = group[0].recipient_contact
        ?.toString()
        .replace(/[+\s-']/g, "");

      const hasSplitTrackingNo = group.some(
        (data) => trackingNumberCount[data.tracking_no] > 1,
      );

      const orderType = hasSplitTrackingNo
        ? ParcelTypeEnums.SPLIT
        : group.length > 1
        ? ParcelTypeEnums.BULK
        : ParcelTypeEnums.INDIVIDUAL;

      const processedOrder = omitBy(
        {
          orderType: orderType,
          senderID: input.senderID,
          senderLocationID: group[0]?.sender_location,
          receiverInfo: omitBy(
            {
              fullName: group[0]?.recipient_name,
              contactEmail: group[0]?.recipient_email,
              contactNumber: group[0]?.recipient_contact && {
                countryCode: recipientContactNumber?.slice(0, 2),
                number: recipientContactNumber?.slice(2),
                fullNumber: recipientContactNumber,
              },
              address: omitBy(parseAddress(group[0], "recipient"), isNil),
            },
            isNil,
          ),
          deliveryType: group[0]?.delivery_type,
          serviceType: group[0]?.service_type,
          specialCare: group[0]?.special_care,
          specialCondition: group[0]?.special_condition,
          weight: group[0]?.weight_in_kg?.toString(),
          currency: group[0]?.currency,
          trackingNumber: group[0]?.tracking_no
            ? group[0]?.tracking_no.toString()
            : undefined,
          dimension: omitBy(
            {
              length: group[0]?.length_in_cm?.toString(),
              width: group[0]?.width_in_cm?.toString(),
              height: group[0]?.height_in_cm?.toString(),
            },
            isNil,
          ),
          hasInsurance: group[0]?.has_insurance,
          remarks: group[0]?.remarks,
          parcels: map(group, (data) =>
            omitBy(
              {
                typeOfGood: data.type_of_goods,
                goodsValue: data.value_of_goods?.toString()?.replace(/,/g, ""),
              },
              isNil,
            ),
          ),
        },
        isNil,
      );
      return processedOrder;
    });

    return resultMap;
  };

  const handleFileLoaded = (data: any, fileInfo: any) => {
    setError({});
    const blueprintedData = { orders: blueprint(data) };
    setIsUploadDisable(true);
    handleBulkValidation(blueprintedData);
  };

  const handleBulkSubmit = () => {
    handleBulkCreate(input);
  };

  const handleSelectChange = (value: string | string[]) => {
    setError(initialError);
    setInput({ ...input, senderID: value });
  };

  const onClearFile = () => {
    const fileList = document.querySelector(".ant-upload-list");
    if (fileList) fileList.innerHTML = "";
    setError(initialError);
    setInput({ ...initialInput, senderID: input.senderID });
    setErrorRow && setErrorRow([]);
    setExcelOrder([]);
    setIsUploadDisable(false);
  };

  const handleDelete = (record) => {
    setErrorRow && setErrorRow([]);
    setExcelOrder((excelOrder) =>
      excelOrder.filter((item) => item._id !== record._id),
    );
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleInfoModalOpen = () => {
    setModal({ open: true, type: "info" });
    setModalContent(
      <MassOrderInfoDisplay orderSettingsInfo={allOrderSettingsInfo} />,
    );
  };

  const handleChargesBreakdownModal = (record) => {
    setModal({ open: true, type: "charges" });
    setModalContent(
      <div>
        <div className="modal-content-title">
          {t("label.amountBreakdown").toLocaleUpperCase()}
        </div>
        <div className="modal-content-text">
          <Row>
            <Col className="label-down" span={18}>
              <span className="label-down">{t("label.baseFare")}</span>
            </Col>
            <Col className="label-down center" span={6}>
              <p className="">
                RM {parseFloat(record?.baseCharges).toFixed(2)}
              </p>
            </Col>
            <Row>
              <Col span={24}>
                <span>{t("label.additionalFare")}</span>
              </Col>
            </Row>
            <Col span={24}>
              {map(record?.additionalCharges, (chargeObj) => {
                return (
                  <Row>
                    <Col span={12}>
                      <span>{ToTitleCase(chargeObj.name)}:</span>
                    </Col>
                    <Col span={12}>
                      <span>RM {parseFloat(chargeObj.amount).toFixed(2)}</span>
                    </Col>
                  </Row>
                );
              })}
            </Col>
          </Row>
          <hr />
          <Row>
            <Col className="label-down" span={18}>
              <span>{t("label.total")}</span>
            </Col>
            <Col className="label-down center" span={6}>
              <p>RM {parseFloat(record?.totalCharges).toFixed(2)}</p>
            </Col>
          </Row>
        </div>
        <div className="my-5 status">{t("txt.chargesInstruction")}</div>
        <div className="mt-30 center done-button">
          <Button
            type={"primary"}
            label={t("action.done")}
            onClick={() => {
              handleResetModalState();
            }}
          />
        </div>
      </div>,
    );
  };

  const highlightErrorRow = (index) => {
    // if an errorRow index is found, set error-row to that row className
    for (let i = 0; i < errorRow.length; i++) {
      if (errorRow[i] == index) return "error-row";
    }

    // otherwise return nothing
    return "";
  };

  //    Render
  // ==========================================================

  return (
    <div>
      <div className="umo-container">
        <div className="umo-header">
          <div className="umo-header-left">
            <SelectInput
              showSearch
              options={map(customerOptions, (customer) => {
                return {
                  label: customer?.name,
                  value: customer?._id,
                };
              })}
              style={{ width: 200 }}
              name="customer"
              placeholder={t("placeholder.filterByCustomer")}
              label={t("label.customer")}
              isRequired={true}
              isReadonly={isUploadDisable}
              onChange={(value) => handleSelectChange(value)}
            />
          </div>
          <div className="umo-header-right">
            <div className="umo-btn-container">
              <Button
                type="secondary"
                label={"template guide"}
                size="large"
                onClick={() => handleInfoModalOpen()}
              />
              <CSVLink data={csvData} filename="upload-mass-order-sample.csv">
                <Button
                  label={t("action.downloadTemplateHere")}
                  icon={<RightCircleOutlined />}
                  type="primary"
                  size="large"
                  onClick={() => {}}
                />
              </CSVLink>
            </div>
          </div>
        </div>
        {isUploadDisable || isEmpty(input.senderID) ? null : (
          <div className="umo-upload-container">
            <div className="umo-upload-component">
              <CSVFileUpload
                name="file"
                text={t("txt.selectExcelFile")}
                hint={t("txt.maxExcelFile")}
                onFileLoaded={handleFileLoaded}
              />
            </div>
            <div className="umo-info umo-help">
              <div>
                <QuestionCircleOutlined /> {t("txt.noteInstructions")}
              </div>
            </div>
          </div>
        )}
        {!isEmpty(error) && (
          <div className="umo-info umo-help">
            <div className="umo-instructions">
              <ExclamationCircleOutlined /> {t("txt.umoError")}
            </div>
            <div className="umo-info-btn">
              <Button
                icon={<UploadOutlined />}
                className="umo-reupload-btn"
                type="link"
                label={"Reupload file"}
                size="large"
                onClick={onClearFile}
              />
            </div>
          </div>
        )}
        {isLoading ? (
          <div className="loading-container">
            <Spin size="large" indicator={<LoadingOutlined />} />
          </div>
        ) : (
          <div className="umo-table">
            <DataTable
              rowKey={(record: any) => {
                return record.key;
              }}
              rowClassName={(record, index) => highlightErrorRow(index)}
              columns={baseColumns}
              dataSource={input.orders}
              scroll={{ x: true }}
            />
            <div className="umo-table-total-container">
              <div className="umo-tt-inner">
                <div className="umo-tt-label">
                  <div className="umo-ttl-title">{t("label.grandTotal")}</div>
                  <div className="umo-ttl-subtitle">
                    {t("txt.inclusiveOfSST")}
                  </div>
                </div>
                <TextInput
                  className="umo-tt-value"
                  isReadonly={true}
                  value={grandTotal}
                  name="grand-total"
                />
              </div>
            </div>
          </div>
        )}
        <div className="umo-bottom-container">
          <Button
            className="mr-10"
            type="link"
            label={t("action.removeFile")}
            size="large"
            onClick={onClearFile}
          />
          <Button
            className="confirmOrder"
            label={t("label.confirmOrder")}
            type="primary"
            size="large"
            isDisabled={isEmpty(excelOrder)}
            onClick={handleBulkSubmit}
          />
        </div>
      </div>
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
        width={modal.type == "info" ? "75vw" : "30vw"}
      >
        {modalContent}
      </BasicModal>
    </div>
  );
};

export default UploadBulkOrderComponent;
